import type { BlogPost } from "./markdown";

export const defaultSiteUrl = "https://rentalledgersheets.com";
export const defaultShareImage = "/blog/mixed-card-spending/hero-v4.jpg";

export type SeoRoute = {
  path: string;
  title: string;
  description: string;
  keywords: string[];
  indexable?: boolean;
};

export type SeoMeta = SeoRoute & {
  canonicalUrl: string;
  image: string;
  type: "website" | "article";
  publishedTime?: string;
  author?: string;
  jsonLd: Record<string, unknown>;
};

export const seoRoutes: SeoRoute[] = [
  {
    path: "/",
    title: "Rental Ledger Sheets | Rental books in Google Sheets",
    description: "Keep using the bank or card you already have, review shared rental spending by property, and hand your CPA a clean export.",
    keywords: ["rental property bookkeeping", "Google Sheets rental ledger", "Schedule E spreadsheet"]
  },
  {
    path: "/sheet",
    title: "Start the Rental Ledger Sheet | Rental Ledger Sheets",
    description: "Open the Google Sheets template preview, copy the Sheet, and check trial access from the Rental Ledger sidebar.",
    keywords: ["rental ledger sheet", "Google Sheets add-on landlord", "rental bookkeeping spreadsheet"]
  },
  {
    path: "/pricing",
    title: "Pricing | Rental Ledger Sheets",
    description: "Paid-only rental ledger pricing: $19/month or $199/year with a 7-day free trial and secure bank/card import.",
    keywords: ["rental ledger pricing", "landlord spreadsheet subscription", "Google Sheets bookkeeping"]
  },
  {
    path: "/support",
    title: "Support | Rental Ledger Sheets",
    description: "Support for trial setup, the rental ledger template, bank/card connection, review rows, billing, and data deletion.",
    keywords: ["rental spreadsheet support", "CPA export support", "landlord bookkeeping help"]
  },
  {
    path: "/setup/start-trial",
    title: "Start Trial From Sheet Sidebar | Rental Ledger Sheets",
    description: "Confirm Google, open secure Stripe Checkout, and return setup to the copied Sheet sidebar.",
    keywords: ["Rental Ledger Sheets trial", "Google Sheets rental ledger trial", "rental ledger Sheet trial"],
    indexable: false
  },
  {
    path: "/setup/teller-connect",
    title: "Connect Bank or Card | Rental Ledger Sheets",
    description: "Connect a bank or card for read-only transaction import into your Rental Ledger Sheets sidebar.",
    keywords: ["connect bank to rental ledger", "Google Sheets bank import", "rental transaction import"],
    indexable: false
  },
  {
    path: "/setup/billing-portal",
    title: "Manage Billing | Rental Ledger Sheets",
    description: "Sign in with Google to open Stripe billing for invoices, payment method updates, plan changes, and cancellation.",
    keywords: ["Rental Ledger Sheets billing", "Stripe billing rental ledger", "Google Sheets subscription"],
    indexable: false
  },
  {
    path: "/rental-property-expense-tracker-google-sheets",
    title: "Rental Property Expense Tracker for Google Sheets",
    description: "Track rental expenses by property, review shared-card rows, and keep Schedule E categories in Google Sheets.",
    keywords: ["rental property expense tracker", "Google Sheets expense tracker", "landlord expense spreadsheet"]
  },
  {
    path: "/schedule-e-spreadsheet",
    title: "Schedule E Spreadsheet for Small Landlords",
    description: "Prepare cleaner Schedule E category totals from a Google Sheets rental ledger built for mixed accounts.",
    keywords: ["Schedule E spreadsheet", "Schedule E rental categories", "rental tax spreadsheet"]
  },
  {
    path: "/landlord-bookkeeping-spreadsheet",
    title: "Landlord Bookkeeping Spreadsheet in Google Sheets",
    description: "A spreadsheet-first rental bookkeeping workflow for landlords who want review queues, rules, and CPA export tabs.",
    keywords: ["landlord bookkeeping spreadsheet", "rental bookkeeping Google Sheets", "CPA export spreadsheet"]
  },
  {
    path: "/blog",
    title: "Rental Bookkeeping Guides | Rental Ledger Sheets",
    description: "Practical Google Sheets guides for mixed cards, bank/card cleanup, Schedule E categories, and CPA-ready rental books.",
    keywords: ["rental bookkeeping guides", "landlord spreadsheet tips", "Schedule E bookkeeping"]
  },
  {
    path: "/privacy",
    title: "Privacy | Rental Ledger Sheets",
    description: "How Rental Ledger Sheets keeps your working ledger in Google Sheets, limits stored data, and handles bank connections, Stripe, Resend, and deletion.",
    keywords: ["Rental Ledger Sheets privacy", "Google Sheets rental ledger privacy", "rental bookkeeping privacy"]
  },
  {
    path: "/terms",
    title: "Terms | Rental Ledger Sheets",
    description: "Terms for using Rental Ledger Sheets as a Google Sheets-native rental ledger, including review responsibility, billing, and cancellation.",
    keywords: ["Rental Ledger Sheets terms", "rental ledger add-on terms"]
  },
  {
    path: "/security",
    title: "Security | Rental Ledger Sheets",
    description: "Security notes for read-only rental bookkeeping connections, Google sign-in, user-owned Sheets, protected credentials, and data controls.",
    keywords: ["Rental Ledger Sheets security", "rental bookkeeping security", "Google Sheets add-on security"]
  }
];

export function normalizeSiteUrl(siteUrl = defaultSiteUrl) {
  return siteUrl.replace(/\/+$/, "");
}

export function canonicalUrl(path: string, siteUrl = defaultSiteUrl) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizeSiteUrl(siteUrl)}${normalizedPath}`;
}

export function getStaticSeoRoute(path: string) {
  return seoRoutes.find((route) => route.path === path) ?? seoRoutes[0];
}

export function buildWebsiteJsonLd(meta: Pick<SeoMeta, "title" | "description" | "canonicalUrl" | "image">) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: meta.title,
    description: meta.description,
    url: meta.canonicalUrl,
    image: meta.image
  };
}

export function buildArticleJsonLd(post: BlogPost, siteUrl = defaultSiteUrl) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    author: {
      "@type": "Organization",
      name: post.author
    },
    image: canonicalUrl(post.heroImage, siteUrl),
    mainEntityOfPage: canonicalUrl(`/blog/${post.slug}`, siteUrl)
  };
}

export function buildSeoMeta(route: SeoRoute, siteUrl = defaultSiteUrl): SeoMeta {
  const image = canonicalUrl(defaultShareImage, siteUrl);
  const canonical = canonicalUrl(route.path, siteUrl);

  return {
    ...route,
    canonicalUrl: canonical,
    image,
    type: "website",
    jsonLd: buildWebsiteJsonLd({
      title: route.title,
      description: route.description,
      canonicalUrl: canonical,
      image
    })
  };
}

export function buildBlogSeoMeta(post: BlogPost, siteUrl = defaultSiteUrl): SeoMeta {
  const path = `/blog/${post.slug}`;
  const image = canonicalUrl(post.heroImage, siteUrl);
  const canonical = canonicalUrl(path, siteUrl);

  return {
    path,
    title: `${post.title} | Rental Ledger Sheets`,
    description: post.description,
    keywords: post.keywords,
    canonicalUrl: canonical,
    image,
    type: "article",
    publishedTime: post.date,
    author: post.author,
    jsonLd: buildArticleJsonLd(post, siteUrl)
  };
}
